import { useEffect, useState } from "react";
import { t } from "@workspace/strings/web";
import {
  openMeteoUrl,
  weatherCacheFresh,
  weatherCodeInfo,
  weatherConfig,
} from "@/player/lib/widgets";

interface Sample {
  temperatureC: number;
  code: number;
  fetchedAtMs: number;
}

const memoryCache = new Map<string, Sample>();

function storageKey(lat: number, lon: number): string {
  return `omnimedia.weather.${lat.toFixed(3)},${lon.toFixed(3)}`;
}

function readStored(key: string): Sample | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Sample;
    if (typeof parsed?.temperatureC !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(key: string, sample: Sample): void {
  try {
    localStorage.setItem(key, JSON.stringify(sample));
  } catch {
    // Best effort.
  }
}

/**
 * Current conditions from Open-Meteo (no API key), cached 15 minutes in memory
 * + localStorage. Offline / fetch failures show the last known sample instead
 * of an error — a store screen should never render a broken weather card.
 */
export function WeatherWidget({
  config,
}: {
  config: Record<string, unknown> | null;
}) {
  const cfg = weatherConfig(config);
  const [sample, setSample] = useState<Sample | null>(() => {
    if (!cfg) return null;
    const key = storageKey(cfg.lat, cfg.lon);
    return memoryCache.get(key) ?? readStored(key);
  });

  useEffect(() => {
    if (!cfg) return;
    const key = storageKey(cfg.lat, cfg.lon);
    let cancelled = false;

    const refresh = async () => {
      const cached = memoryCache.get(key) ?? readStored(key);
      if (cached && weatherCacheFresh(cached.fetchedAtMs, Date.now())) {
        if (!cancelled) setSample(cached);
        return;
      }
      try {
        const res = await fetch(openMeteoUrl(cfg));
        if (!res.ok) throw new Error(`weather ${res.status}`);
        const body = (await res.json()) as {
          current?: { temperature_2m?: number; weather_code?: number };
        };
        const temperatureC = body.current?.temperature_2m;
        if (typeof temperatureC !== "number") throw new Error("no temp");
        const next: Sample = {
          temperatureC,
          code: body.current?.weather_code ?? -1,
          fetchedAtMs: Date.now(),
        };
        memoryCache.set(key, next);
        writeStored(key, next);
        if (!cancelled) setSample(next);
      } catch {
        // Graceful offline: keep showing whatever we had.
        if (!cancelled && cached) setSample(cached);
      }
    };

    void refresh();
    const t = setInterval(() => void refresh(), 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.lat, cfg?.lon]);

  if (!cfg) {
    return (
      <div
        className="flex h-full w-full items-center justify-center bg-background text-muted-foreground"
        data-testid="widget-weather"
      >
        {t("player.widgets.weather.needsLocation")}
      </div>
    );
  }

  const info = sample ? weatherCodeInfo(sample.code) : null;
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-3 bg-background"
      data-testid="widget-weather"
    >
      {cfg.label && (
        <div className="text-muted-foreground text-[3vw]">{cfg.label}</div>
      )}
      {sample ? (
        <>
          <div className="text-[10vw] leading-none">{info!.glyph}</div>
          <div className="font-semibold tabular-nums text-foreground text-[10vw] leading-none">
            {Math.round(sample.temperatureC)}°C
          </div>
          <div className="text-muted-foreground text-[3vw]">{info!.label}</div>
        </>
      ) : (
        <div className="text-muted-foreground text-[3vw]">
          {t("player.widgets.weather.loading")}
        </div>
      )}
    </div>
  );
}
